import React, { useState, useEffect } from 'react';
import { X, Printer, Clock, HeartPulse, Phone, AlertTriangle, Baby } from 'lucide-react';
import { enfantsApi } from '../../utils/api';

const SECTIONS = [
  { key: 'presence', label: 'Présence', icon: Clock },
  { key: 'sante', label: 'Santé', icon: HeartPulse },
  { key: 'contacts', label: 'Contacts', icon: Phone },
];

export default function FicheEquipe({ enfantId, onClose }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(['presence', 'sante', 'contacts']);

  useEffect(() => {
    enfantsApi.getFiche(enfantId).then(r => setData(r.data)).catch(() => setData(false));
  }, [enfantId]);

  const toggle = (k) => setSel(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  const has = (k) => sel.includes(k);

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-sheet" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gold-600 font-semibold">Fiche de transmission</div>
            <h3 className="font-serif text-[21px] text-navy-900">
              {data ? `${data.enfant.prenom} ${data.enfant.nom}` : 'Chargement…'}
            </h3>
            {data && <p className="text-[12px] text-navy-300">{data.enfant.structure_nom}{data.enfant.date_naissance ? ` · né(e) le ${data.enfant.date_naissance}` : ''}</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400"><X size={17} /></button>
        </div>

        {/* Sélection des sections */}
        <div className="px-6 pb-3 flex flex-wrap gap-2 flex-shrink-0">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => toggle(s.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${has(s.key) ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-400 border-cream-300'}`}>
              <s.icon size={13} /> {s.label}
            </button>
          ))}
        </div>

        {/* Contenu consultable */}
        <div className="px-6 pb-4 overflow-auto flex-1 space-y-4">
          {data === false && <p className="text-red-500 text-sm">Fiche indisponible.</p>}
          {data && (
            <>
              {has('presence') && (
                <Section icon={Clock} title="Présence">
                  {data.presence.length ? (
                    <div className="space-y-1">
                      {data.presence.map((p, i) => (
                        <div key={i} className="flex justify-between text-[13.5px]">
                          <span className="text-navy-700 font-medium">{p.jour}</span>
                          <span className="text-navy-500 tabular-nums">{p.debut} – {p.fin}</span>
                        </div>
                      ))}
                    </div>
                  ) : <Empty>Aucun planning défini.</Empty>}
                </Section>
              )}

              {has('sante') && (
                <Section icon={HeartPulse} title="Santé">
                  {data.sante.pai && (
                    <div className="flex items-center gap-1.5 text-[13px] text-red-700 bg-red-50 rounded-lg px-3 py-2 mb-2 font-medium">
                      <AlertTriangle size={14} /> PAI en place — consulter le protocole
                    </div>
                  )}
                  <Field label="Allergies" value={data.sante.allergies} danger={!!data.sante.allergies} fallback="Aucune signalée" />
                  {data.sante.antecedents && <Field label="Antécédents" value={data.sante.antecedents} />}
                  <Field label="Médecin" value={[data.sante.medecin_nom, data.sante.medecin_tel].filter(Boolean).join(' · ')} fallback="—" />
                </Section>
              )}

              {has('contacts') && (
                <Section icon={Phone} title="Contacts & personnes autorisées">
                  {data.contacts.length ? data.contacts.map((c, i) => (
                    <div key={i} className="py-1.5 border-b border-cream-200 last:border-0">
                      <div className="text-[13.5px] font-medium text-navy-800">{c.prenom} {c.nom} <span className="text-[11px] text-navy-300 font-normal">· {c.lien || (c.type === 'parent1' ? 'Parent 1' : c.type === 'parent2' ? 'Parent 2' : c.type)}</span></div>
                      <div className="text-[12.5px] text-navy-500">{c.telephone_portable || c.telephone_pro || '—'}{c.email ? ` · ${c.email}` : ''}</div>
                    </div>
                  )) : <Empty>Aucun contact enregistré.</Empty>}
                </Section>
              )}

              {sel.length === 0 && <p className="text-[13px] text-navy-300 text-center py-6">Sélectionnez au moins une section.</p>}
            </>
          )}
        </div>

        {/* Pied : impression */}
        <div className="px-6 py-4 border-t border-cream-200 flex justify-between items-center flex-shrink-0">
          <span className="text-[11px] text-navy-300 flex items-center gap-1"><Baby size={12} /> Sans information financière</span>
          <a href={data ? enfantsApi.getFichePdfUrl(enfantId, sel.length ? sel : ['presence']) : '#'} target="_blank" rel="noopener noreferrer"
            className="apple-btn-gold"><Printer size={15} /> Imprimer</a>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-cream-100 border border-cream-300/60 rounded-2xl p-4">
      <div className="text-[11px] uppercase tracking-wide text-gold-600 font-semibold mb-2 flex items-center gap-1.5"><Icon size={12} /> {title}</div>
      {children}
    </div>
  );
}
function Field({ label, value, danger, fallback }) {
  return (
    <div className="text-[13.5px] py-0.5">
      <span className="text-navy-400">{label} : </span>
      <span className={danger ? 'text-red-600 font-medium' : 'text-navy-800'}>{value || fallback}</span>
    </div>
  );
}
function Empty({ children }) { return <p className="text-[13px] text-navy-300">{children}</p>; }
